// Teacher Profile Tab - SAT V4 Zone
import { DsButton, DsCard, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import { Avatar, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import type { Teacher } from '../../../../types';
import { roleColors, statusColors } from '../../MockData';

const AVAILABLE_ROLES = ['Head of department', 'Head of Year', 'SLT', 'SEND', 'Safeguarding', 'Behavior', 'Attendance'] as const;
const STATUSES = ['active', 'inactive', 'on_leave'] as const;
const TITLES = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof'] as const;

interface ProfileTabProps {
  teacher: Teacher;
  onUpdate?: (updates: Partial<Teacher>) => void;
}

const ProfileTab: FC<ProfileTabProps> = ({ teacher, onUpdate }) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: teacher.title,
    firstName: teacher.firstName,
    lastName: teacher.lastName,
    email: teacher.email,
    phone: teacher.phone || '',
    roles: teacher.roles,
    department: teacher.department || '',
    status: teacher.status,
    maxPeriodsPerWeek: teacher.maxPeriodsPerWeek,
  });

  const handleOpenEdit = () => {
    setFormData({
      title: teacher.title,
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      email: teacher.email,
      phone: teacher.phone || '',
      roles: teacher.roles,
      department: teacher.department || '',
      status: teacher.status,
      maxPeriodsPerWeek: teacher.maxPeriodsPerWeek,
    });
    setEditModalOpen(true);
  };

  const handleSave = () => {
    onUpdate?.(formData);
    setEditModalOpen(false);
  };
  return (
    <Stack spacing={3}>
      {/* Header Card */}
      <DsCard sx={{ p: 3 }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Avatar
            sx={{
              width: 100,
              height: 100,
              bgcolor: 'primary.main',
              color: 'white',
              fontSize: 32,
            }}
          >
            {teacher.firstName[0]}{teacher.lastName[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <DsTypography variant="h5" fontWeight={700}>
              {teacher.title} {teacher.firstName} {teacher.lastName}
            </DsTypography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              {teacher.roles.length > 0 && teacher.roles.map((role, idx) => (
                <Chip
                  key={idx}
                  label={role}
                  sx={{
                    bgcolor: `${roleColors[role] || '#9E9E9E'}20`,
                    color: roleColors[role] || '#9E9E9E',
                  }}
                />
              ))}
              <Chip
                label={teacher.status.replace('_', ' ')}
                sx={{
                  bgcolor: `${statusColors[teacher.status]}20`,
                  color: statusColors[teacher.status],
                  textTransform: 'capitalize',
                }}
              />
              <Chip label={teacher.employeeId} variant="outlined" />
            </Stack>
          </Box>
          {onUpdate && (
            <DsButton
              variant="contained"
              startIcon={<DsIcon icon="EDIT" />}
              onClick={handleOpenEdit}
            >
              Edit Profile
            </DsButton>
          )}
        </Stack>
      </DsCard>

      {/* Personal Information */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Personal Information
        </DsTypography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Title"
              value={teacher.title}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              fullWidth
              label="First Name"
              value={teacher.firstName}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              fullWidth
              label="Last Name"
              value={teacher.lastName}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              value={teacher.email}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={teacher.phone || ''}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
        </Grid>
      </DsCard>

      {/* Employment Information */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Employment Information
        </DsTypography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Employee ID"
              value={teacher.employeeId}
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Roles"
              value={teacher.roles.length > 0 ? teacher.roles.join(', ') : 'No roles assigned'}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Department"
              value={teacher.department || 'Not Assigned'}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Hire Date"
              value={new Date(teacher.hireDate).toLocaleDateString()}
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Status"
              value={teacher.status.replace('_', ' ')}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
              sx={{ textTransform: 'capitalize' }}
            />
          </Grid>
        </Grid>
      </DsCard>

      {/* Quick Stats */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Quick Stats
        </DsTypography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <DsTypography variant="h4" fontWeight={700} color="primary.main">
                {teacher.subjects.length}
              </DsTypography>
              <DsTypography variant="caption" color="text.secondary">
                Subjects
              </DsTypography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <DsTypography variant="h4" fontWeight={700} color="success.main">
                {teacher.currentPeriodsAssigned}
              </DsTypography>
              <DsTypography variant="caption" color="text.secondary">
                Current Periods
              </DsTypography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <DsTypography variant="h4" fontWeight={700} color="info.main">
                {teacher.maxPeriodsPerWeek}
              </DsTypography>
              <DsTypography variant="caption" color="text.secondary">
                Max Periods/Week
              </DsTypography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <DsTypography variant="h4" fontWeight={700} color="secondary.main">
                {teacher.preferences?.length || 0}
              </DsTypography>
              <DsTypography variant="caption" color="text.secondary">
                Preferences
              </DsTypography>
            </Box>
          </Grid>
        </Grid>
      </DsCard>

      {/* Edit Profile Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Teacher Profile</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Title</InputLabel>
                  <Select
                    value={formData.title}
                    label="Title"
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  >
                    {TITLES.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Roles</InputLabel>
                  <Select
                    multiple
                    value={formData.roles}
                    label="Roles"
                    onChange={(e) => setFormData({ ...formData, roles: e.target.value as Teacher['roles'] })}
                    renderValue={(selected) => <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>{selected.map((role, idx) => <Chip key={idx} label={role} size="small" />)}</Stack>}
                  >
                    {AVAILABLE_ROLES.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Department"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as Teacher['status'] })}
                  >
                    {STATUSES.map((s) => (
                      <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                        {s.replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              size="small"
              label="Max Periods Per Week"
              type="number"
              value={formData.maxPeriodsPerWeek}
              onChange={(e) => setFormData({ ...formData, maxPeriodsPerWeek: Number.parseInt(e.target.value) || 0 })}
              inputProps={{ min: 1, max: 50 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setEditModalOpen(false)}>Cancel</DsButton>
          <DsButton variant="contained" onClick={handleSave}>Save Changes</DsButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ProfileTab;

